import type { MonitoringTraysView, TrayName } from '@ctcells/api'
import { createContext } from 'react'

import type { RotorSlotView } from '../../figures/RotorFigure.tsx'
import { COUNTERWEIGHT_MARK, type TrayCellView } from '../../figures/TrayFigure.tsx'

import { TRAY_SLOTS, type MonitoringDeviceKey } from './monitoringModel.ts'

/**
 * **서버 트레이 뷰**(`GET /monitoring/trays` · 정본 선행) → 모니터링 그림 재료(eric 2026-10-07 「C · A · L 트레이 하나하나 · 원심분리기에는 뭐가 있고 ·
 * 실제 챔버별로 각각 상태」).
 *
 * 🔴 **종전 임시 흐름(`temporaryStepMock.temporaryChamberFlow`)을 갈음한다** — 그쪽은 지금 실행의 스텝을 칸 수로 나눈 나머지로
 *    자리를 지어 내 결과 트레이 ↔ 시작 트레이를 왔다 갔다 했다. 서버 뷰는 쌓이는 실행 기록으로 세어 **앞으로만** 간다.
 * 🔴 모니터링 컨테이너(live)만 이 값을 내린다(`TrayViewContext`). 없으면(목 화면 · 그림만 그리는 자리) 그림은 종전대로다.
 */
export const TrayViewContext = createContext<MonitoringTraysView | undefined>(undefined)

/**
 * 염색기 «직전 분주 칸»(`GET /dispense-records` 한 바퀴 지도의 첫 키 · `currentDispensePass`) — 모니터링 컨테이너(live)가 내린다.
 * 염색기는 셰이커 위 챔버 전부를 ▶ 로 내서 트레이 뷰로는 어느 칸을 다루는지 못 가르므로 정면도의 피펫 툴이 이 칸으로 간다
 * (eric 2026-10-08 「각각의 트레이 위치에 가야 되는데 지금 P1 에만」). 없으면 `undefined`(짐작하지 않는다)
 */
export const StainLatestWellContext = createContext<string | undefined>(undefined)

/** 장비마다 «작업 전 / 작업 후» 트레이 — `TRAY_LABEL_KEY` 와 같은 짝(염색기는 L 트레이 한 장이 자리만 옮긴다) */
const TRAY_OF: Record<MonitoringDeviceKey, { pre: TrayName; post: TrayName }> = {
  cent: { pre: 'C', post: 'A' },
  asm: { pre: 'A', post: 'L' },
  stain: { pre: 'L', post: 'L' },
  robot: { pre: 'C', post: 'A' },
}

/** 그 트레이를 떠났다는 길 표지 — 시작 트레이 칸에 「옮겨 갔다」(✓)를 단다 */
const LEFT_BY: Record<TrayName, string> = { C: 'C→rotor', A: 'A→tilt', L: '' }

export function trayNameFor(deviceKey: MonitoringDeviceKey, side: 'pre' | 'post'): TrayName {
  return TRAY_OF[deviceKey][side]
}

/**
 * 트레이 그림 칸 — 그 트레이에 지금 있는 챔버는 `present`, 그 트레이를 떠난 챔버는 `moved`(✓), 나머지는 빈 자리.
 * 염색기는 한 장(L)이 셰이커(작업 중 · 앞) → 끝나면 앞자리로 — 작업 전 칸은 셰이커 위, 작업 후 칸은 끝난 것.
 */
export function trayViewCells(
  view: MonitoringTraysView,
  deviceKey: MonitoringDeviceKey,
  side: 'pre' | 'post',
): TrayCellView[] {
  const name = trayNameFor(deviceKey, side)
  const tray = view.trays.find((item) => item.tray === name)
  const byWell = new Map(view.chambers.map((chamber) => [chamber.well, chamber]))
  return TRAY_SLOTS.map((slot) => {
    const well = tray?.wells[slot]
    const chamber = byWell.get(slot)
    const reference = well?.reference === true || chamber?.reference === true
    const ref = well?.sample_ref ?? chamber?.sample_ref ?? null
    /* 챔버 든 칸의 공통 칸 — 시료 표지(이름) · 대조 여부 */
    const named = {
      ...(reference ? { reference: true } : {}),
      ...(ref === null ? {} : { label: ref }),
    }
    if (deviceKey === 'stain') {
      const working = chamber?.tray === 'L' && chamber.place === 'shaker'
      const done =
        chamber?.tray === 'L' && chamber.place === 'tray' && chamber.stage === 'completed'
      const present = side === 'pre' ? working : done
      /*
       * 🔴 셰이커 위 칸 — 서버가 «지금 분주 중»이라 말한 웰(`staining.active_wells` · 또는 챔버 `active`)만 ▶, 나머지는 ◌ 대기.
       *    종전(~2026-10-07)에는 셰이커 위 전부를 ▶ 로 칠해 어느 칸을 다루는지 안 보였다(eric 「시뮬레이터를 보고 추가할 것」 중 2).
       *    염색까지 끝난 칸은 ✓.
       */
      const activeNow =
        chamber?.active === true || (view.staining?.active_wells ?? []).includes(slot)
      return present
        ? {
            slot,
            present,
            verdict:
              side === 'pre'
                ? activeNow
                  ? ('active' as const)
                  : ('pending' as const)
                : ('pass' as const),
            ...named,
          }
        : { slot, present, ...(reference ? { reference: true } : {}) }
    }
    if (well?.present === true)
      /*
       * 🔴 표지는 서버 값 그대로 — `active`(지금 그 공정이 다루는 중 · 로터로 싣는 중 · 틸트로 집는 중) ▶ ·
       *    출발 트레이에서 기다리는 챔버 ◌ · 목표 트레이에 닿은 챔버 ✓. 여기서 순서를 셈하지 않는다.
       * 🔴 출발 트레이의 비전 검사(서버 `tray.inspected` — 입고 트레이를 «한 번에» 보는 `c02_inspect_inlet`)가 통과면 기다리는
       *    챔버도 ✓(비전 통과), 실패면 ✗ — 검사 결과도 서버 값이다(eric 2026-10-07 「한꺼번에 비전 처리하는 스텝.. 초록색 체크 아이콘
       *    그것을 넣어줘」). 검사 기록이 없거나 검사 중이면 종전대로 ◌.
       */
      return {
        slot,
        present: true,
        verdict:
          chamber?.active === true
            ? 'active'
            : side === 'post'
              ? 'pass'
              : tray?.inspected?.status === 'passed'
                ? 'pass'
                : tray?.inspected?.status === 'failed'
                  ? 'fail'
                  : 'pending',
        ...named,
      }
    const left = LEFT_BY[name]
    if (side === 'pre' && left !== '' && chamber?.path.includes(left) === true)
      return { slot, present: false, verdict: 'moved' as const }
    return { slot, present: false }
  })
}

/** 그 트레이의 챔버 수 / 칸 수 — 그림 이름표 옆 「3/9」. 트레이 뷰에 그 트레이가 없으면 `undefined` */
export function trayViewCount(
  view: MonitoringTraysView,
  deviceKey: MonitoringDeviceKey,
  side: 'pre' | 'post',
): { count: number; capacity: number } | undefined {
  const tray = view.trays.find((item) => item.tray === trayNameFor(deviceKey, side))
  if (tray === undefined) return undefined
  if (deviceKey === 'stain') {
    const count = trayViewCells(view, deviceKey, side).filter((cell) => cell.present).length
    return { count, capacity: tray.capacity }
  }
  return { count: tray.count, capacity: tray.capacity }
}

/** 로터 마주보는 자리(1↔3 · 2↔4) — 평형추는 홀로 실린 챔버의 맞은편에 앉는다(균형) */
const OPPOSITE: Readonly<Record<1 | 2 | 3 | 4, 1 | 2 | 3 | 4>> = { 1: 3, 2: 4, 3: 1, 4: 2 }

/**
 * 원심 로터 네 자리 — 서버가 말한 자리에 그 웰. 회전 중이거나 그 챔버를 다루는 중이면 `active`.
 *
 * 🔴 **평형추(R″)도 로터에 그린다**(eric 2026-10-08 「ref 랑 무게 중심추 원심분리기에서 안 돌아」). 서버 뷰는 `centrifuge.counterweight.where`
 *    로 「로터에 있다」만 말하고 칸 번호는 안 준다 — Reference 회차는 챔버 하나(REF)만 실리므로 평형추는 그 **맞은편**(`OPPOSITE`)이다.
 *    서버가 칸(`counterweight.slot`)을 주면 그 값이다 — dev2 판은 평형추 걸음(`c42_ref_place_cw_s3`)으로 REF 보다 «먼저» 칸 3에 앉힌다
 *    (eric 2026-10-08 「무게 중심추 원심분리기로 옮기는 것이 없어」). 칸이 없는 옛 판만 맞은편 규칙이다. 실린 챔버가 하나가 아니면
 *    (균형이 맞아 평형추가 없는 회차) 그리지 않는다 — 짐작한 자리에 세우지 않는다.
 */
export function trayViewRotor(view: MonitoringTraysView): RotorSlotView[] {
  const rotor = view.centrifuge?.rotor ?? []
  const spinning = view.centrifuge?.spinning === true
  /*
   * 「지금 다루는 칸」 — 서버가 «지금 걸음»의 챔버를 말하면(`active_well`) 그 하나. 옛 판(키 없음)은 챔버 `active`(원심에서는 로터에
   * 실린 것 전부가 참) — 그때는 실린 칸이 다 «지금»으로 칠해진다(서버 값 그대로).
   */
  const current = view.centrifuge?.active_well
  const active = new Set(
    current !== undefined
      ? current === null
        ? []
        : [current]
      : view.chambers.filter((chamber) => chamber.active).map((c) => c.well),
  )
  const lone = rotor.length === 1 ? rotor[0] : undefined
  const cw = view.centrifuge?.counterweight
  /* 서버가 칸을 말하면(평형추 걸음을 걸은 판) 그 칸 · 아니면 홀로 실린 챔버의 맞은편 */
  const counterweightAt =
    cw?.where !== 'rotor'
      ? undefined
      : typeof cw.slot === 'number'
        ? cw.slot
        : lone !== undefined
          ? OPPOSITE[lone.slot as 1 | 2 | 3 | 4]
          : undefined
  return ([1, 2, 3, 4] as const).map((index) => {
    const seat = rotor.find((item) => item.slot === index)
    if (seat !== undefined)
      return { index, slot: seat.well, loaded: true, active: spinning || active.has(seat.well) }
    if (index === counterweightAt)
      return {
        index,
        slot: COUNTERWEIGHT_MARK,
        loaded: true,
        active: spinning || cw?.active === true,
        counterweight: true,
      }
    return { index, loaded: false }
  })
}

/** 어셈블러 틸트 스테이지 위 챔버(없으면 `undefined`) */
export function trayViewAssemblerSlot(view: MonitoringTraysView): string | undefined {
  return view.chambers.find((chamber) => chamber.place === 'tilt_stage')?.well
}
